import { createSlice } from "@reduxjs/toolkit";
import {
  getHomeDetails,
  getAllCMSByURL,
  getAllMetaData,
  getAllActiveSettings,
  getAllActiveBanner,
  insertInquiry,
  getAllActiveGallerys,
  getAllActiveRooms,
  getAllActiveTestimonials,
  getAllActiveAmenities,
  emailSubscribe,
} from "../services/home.services";

export interface HomeState {
  loading: boolean;
  galleryloading: boolean;
  roomLoading: boolean;
  testimonialLoading: boolean;
  newsletterLoading: boolean;
  amenitiesLoading: boolean;
  formLoading: boolean;
  error: string;
  homeDetails: any;
  CMSList: any[];
  BannerList: any[];
  SettingList: any[];
  MetaDetails: {
    MetaTitle: string;
    MetaKeywords: string;
    MetaDescription: string;
  };
  GalleryList: any[];
  RoomList: any[];
  TestimonialList: any[];
  AmenitiesList: any[];
}

export interface ICMS {
  ID: number | null;
  Name: string;
  CMSURL: string;
  CMSImage: string;
  CMSImageAlterTag?: string;
  Template: string;
  MetaTitle: string;
  MetaKeyword: string;
  MetaDescription: string;
  cms_image_details: any[];
}

export interface ISettings {
  ID: number | null;
  SettingKey: string;
  SettingType: string;
  SettingValue: string;
  AttachmentFile: string;
  addressmap: string;
}

const initialState: HomeState = {
  loading: false,
  galleryloading: false,
  roomLoading: false,
  testimonialLoading: false,
  amenitiesLoading: false,
  newsletterLoading: false,
  formLoading: false,
  error: "",
  homeDetails: null,
  CMSList: [],
  BannerList: [],
  SettingList: [],
  MetaDetails: {
    MetaTitle: "",
    MetaKeywords: "",
    MetaDescription: "",
  },
  GalleryList: [],
  RoomList: [],
  TestimonialList: [],
  AmenitiesList: [],
};

const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Home Details
    builder.addCase(getHomeDetails.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getHomeDetails.fulfilled, (state, action) => {
      state.loading = false;
      state.homeDetails = action.payload;
    });
    builder.addCase(getHomeDetails.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // CMS
    builder.addCase(getAllCMSByURL.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllCMSByURL.fulfilled, (state, action) => {
      state.loading = false;
      state.CMSList = action.payload;
    });
    builder.addCase(getAllCMSByURL.rejected, (state, action) => {
      state.loading = false;
    });

    // Banner
    builder.addCase(getAllActiveBanner.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllActiveBanner.fulfilled, (state, action) => {
      state.loading = false;
      state.BannerList = action.payload;
    });
    builder.addCase(getAllActiveBanner.rejected, (state, action) => {
      state.loading = false;
    });

    // Rooms
    builder.addCase(getAllActiveRooms.pending, (state) => {
      state.roomLoading = true;
    });
    builder.addCase(getAllActiveRooms.fulfilled, (state, action) => {
      state.roomLoading = false;
      state.RoomList = action.payload;
    });
    builder.addCase(getAllActiveRooms.rejected, (state, action) => {
      state.roomLoading = false;
    });

    // Amenities
    builder.addCase(getAllActiveAmenities.pending, (state) => {
      state.amenitiesLoading = true;
    });
    builder.addCase(getAllActiveAmenities.fulfilled, (state, action) => {
      state.amenitiesLoading = false;
      state.AmenitiesList = action.payload;
    });
    builder.addCase(getAllActiveAmenities.rejected, (state, action) => {
      state.amenitiesLoading = false;
    });

    // Gallery
    builder.addCase(getAllActiveGallerys.pending, (state) => {
      state.galleryloading = true;
    });
    builder.addCase(getAllActiveGallerys.fulfilled, (state, action) => {
      state.galleryloading = false;
      state.GalleryList = action.payload;
    });
    builder.addCase(getAllActiveGallerys.rejected, (state, action) => {
      state.galleryloading = false;
    });

    // Testimonials
    builder.addCase(getAllActiveTestimonials.pending, (state) => {
      state.testimonialLoading = true;
    });
    builder.addCase(getAllActiveTestimonials.fulfilled, (state, action) => {
      state.testimonialLoading = false;
      state.TestimonialList = action.payload;
    });
    builder.addCase(getAllActiveTestimonials.rejected, (state, action) => {
      state.testimonialLoading = false;
    });

    // Meta Data
    builder.addCase(getAllMetaData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllMetaData.fulfilled, (state, action) => {
      state.loading = false;
      state.MetaDetails = action.payload;
    });
    builder.addCase(getAllMetaData.rejected, (state, action) => {
      state.loading = false;
    });

    // Settings
    builder.addCase(getAllActiveSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllActiveSettings.fulfilled, (state, action) => {
      state.loading = false;
      state.SettingList = action.payload;
    });
    builder.addCase(getAllActiveSettings.rejected, (state, action) => {
      state.loading = false;
    });

    // Inquiry
    builder.addCase(insertInquiry.pending, (state) => {
      state.formLoading = true;
    });
    builder.addCase(insertInquiry.fulfilled, (state, action) => {
      state.formLoading = false;
    });
    builder.addCase(insertInquiry.rejected, (state, action) => {
      state.formLoading = false;
    });

    builder.addCase(emailSubscribe.pending, (state) => {
      state.newsletterLoading = true;
      state.error = "";
    });
    builder.addCase(emailSubscribe.fulfilled, (state) => {
      state.newsletterLoading = false;
    });
    builder.addCase(emailSubscribe.rejected, (state, action) => {
      state.newsletterLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export default homeSlice.reducer;
